import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useMutation, useQueryClient } from 'react-query';

import axiosClient from '../api/axios-client';
import { invalidatePrefixes } from '../api/invalidatePrefixes';
import type { ApiError } from '../api/useApi';
import { compressImageWithDetails } from '../utils/imageCompression';
import { useVoiceRecorder } from './useVoiceRecorder';
import type { RecordedAudio } from './useVoiceRecorder';

/**
 * Signalement « photo d'abord » des agents d'entretien, partagé par le web
 * (`pages/housekeeping/my-rooms`) et l'app mobile (`mobile/screens/menage`).
 *
 * L'agent ne tape rien : photo, chambre, éventuellement « urgent ». Le serveur
 * complète titre, nom, catégorie et lieu (HousekeepingReportDefaults).
 *
 * L'<input type="file"> appartient à l'écran qui affiche les tuiles, pas à la
 * fenêtre qui s'ouvre ensuite : iOS n'ouvre l'appareil photo que dans le geste
 * de l'utilisateur, donc `click()` doit partir du tap lui-même, jamais d'un effet.
 */
type QuickReportKind = 'issue' | 'lost';

type QuickReportStep = 'idle' | 'room' | 'review' | 'done';

interface QuickReportSend {
    urgent?: boolean;
    detail?: string;
}

interface Payload extends QuickReportSend {
    kind: QuickReportKind;
    file: File;
    serviceId: number | null;
    audio?: RecordedAudio | null;
}

/** Durée de l'écran « envoyé » avant le retour automatique. */
const QUICK_REPORT_DONE_MS = 1600;

async function uploadFile(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await axiosClient.post<{ url: string }>('/files?secure=false', formData);
    return data.url;
}

async function sendReport({ kind, file, serviceId, urgent, detail, audio }: Payload): Promise<void> {
    const [photoUrl, audioUrl] = await Promise.all([
        compressImageWithDetails(file).then(({ file: compressed }) => uploadFile(compressed)),
        // Le message vocal part tel quel : le serveur le transcrit et le traduit en français.
        audio ? uploadFile(new File([audio.blob], `voice.${audio.extension}`, { type: audio.blob.type })) : null,
    ]);

    await axiosClient.post(kind === 'issue' ? '/housekeeping/technical-issues' : '/housekeeping/lost-items', {
        service_id: serviceId,
        photo_url: photoUrl,
        ...(audioUrl ? { audio_url: audioUrl } : {}),
        description: detail?.trim() || undefined,
        ...(kind === 'issue' && urgent ? { priority: 'critical' } : {}),
    });
}

export function useHousekeepingQuickReport() {
    const queryClient = useQueryClient();
    const inputRef = useRef<HTMLInputElement>(null);
    const [step, setStep] = useState<QuickReportStep>('idle');
    const [kind, setKind] = useState<QuickReportKind>('issue');
    const [serviceId, setServiceId] = useState<number | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const voice = useVoiceRecorder();
    const isStoppingRef = useRef(false);

    const mutation = useMutation<void, ApiError, Payload>(sendReport, {
        // Une panne ne change que les pannes ; un objet trouvé suit les chambres (comme useCreateLostItem).
        onSuccess: (_data, { kind: sentKind }) => {
            invalidatePrefixes(queryClient, sentKind === 'issue' ? '/housekeeping/technical-issues' : '/housekeeping');
            navigator.vibrate?.(80);
            setStep('done');
        },
    });

    useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

    useEffect(() => {
        if (step !== 'done') return undefined;
        const timer = window.setTimeout(() => reset(), QUICK_REPORT_DONE_MS);
        return () => window.clearTimeout(timer);
    }, [step]);

    const openCamera = () => {
        if (inputRef.current) inputRef.current.value = '';
        inputRef.current?.click();
    };

    const pickRoom = (roomId: number | null) => {
        setServiceId(roomId);
        openCamera();
    };

    /** `roomId` absent = l'agent choisit d'abord la chambre ; `null` = hors chambre. */
    const start = (nextKind: QuickReportKind, roomId?: number | null) => {
        setKind(nextKind);
        if (roomId === undefined) setStep('room');
        else pickRoom(roomId);
    };

    const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const picked = e.target.files?.[0];
        if (!picked) return;
        setFile(picked);
        setPreviewUrl(URL.createObjectURL(picked));
        mutation.reset();
        setStep('review');
    };

    /** Envoyer pendant l'enregistrement l'arrête et joint le message. */
    const send = async (options: QuickReportSend = {}) => {
        if (!file || mutation.isLoading || isStoppingRef.current) return;
        isStoppingRef.current = true;
        const audio = await voice.stop();
        isStoppingRef.current = false;
        mutation.mutate({ kind, file, serviceId, audio, ...options });
    };

    function reset() {
        setStep('idle');
        setFile(null);
        setPreviewUrl(null);
        setServiceId(null);
        voice.clear();
        mutation.reset();
    }

    return {
        /** À étaler sur l'<input> caché de l'écran des tuiles. */
        inputProps: {
            ref: inputRef,
            type: 'file',
            accept: 'image/*',
            capture: 'environment',
            onChange: onFileChange,
            className: 'hidden',
        } as const,
        step,
        isReviewOpen: step === 'review' || step === 'done',
        kind,
        previewUrl,
        isSending: mutation.isLoading,
        isError: mutation.isError,
        start,
        pickRoom,
        retake: openCamera,
        send,
        cancel: reset,
        /** Faux sans MediaRecorder ni micro : le bouton n'est pas affiché. */
        recordingSupported: voice.isSupported,
        isRecording: voice.isRecording,
        recordingSeconds: voice.seconds,
        recordingError: voice.error,
        audioPreviewUrl: voice.audio?.url ?? null,
        toggleRecording: voice.toggle,
        clearAudio: voice.clear,
    };
}

export type HousekeepingQuickReport = ReturnType<typeof useHousekeepingQuickReport>;
